// API Configuration
// All API endpoints and environment settings
// Uses Vite environment variables (import.meta.env)

const env = import.meta.env || {};

// Get API base URL from environment variables or use default
const API_BASE_URL = env.VITE_API_BASE_URL || 'http://localhost:8080';

// Get app info from environment variables or use defaults
const APP_NAME = env.VITE_APP_NAME || 'QuizBrew';
const APP_VERSION = env.VITE_APP_VERSION || '1.0.0';

// Feature flags from environment
const FEATURES = {
    analytics: env.VITE_ENABLE_ANALYTICS !== 'false',
    scheduling: true,
    sharing: true,
    export: true,
    templates: true,
    apiAccess: env.VITE_ENABLE_API !== 'false',
    themeTaxonomyRead: env.VITE_FF_THEME_TAXONOMY_READ !== 'false',
    themeTaxonomyWrite: env.VITE_FF_THEME_TAXONOMY_WRITE !== 'false',
    themeSearchStrict: env.VITE_FF_THEME_SEARCH_STRICT !== 'false',
    themeReuseRanking: env.VITE_FF_THEME_REUSE_RANKING !== 'false',
    structuredEnrichment: env.VITE_FF_STRUCTURED_ENRICHMENT !== 'false',
    themeAnalytics: env.VITE_FF_THEME_ANALYTICS !== 'false',
    superadmin: env.VITE_FF_SUPERADMIN !== 'false',
    adminCms: env.VITE_FF_ADMIN_CMS !== 'false',
    adminCrm: env.VITE_FF_ADMIN_CRM !== 'false',
    adminCampaigns: env.VITE_FF_ADMIN_CAMPAIGNS !== 'false',
    leadCapture: env.VITE_FF_LEAD_CAPTURE !== 'false',
    publicDirectory: env.VITE_FF_PUBLIC_DIRECTORY !== 'false',
    venueSocialAssets: env.VITE_FF_VENUE_SOCIAL_ASSETS !== 'false',
    digitalPlay: env.VITE_FF_DIGITAL_PLAY !== 'false',
    digitalPlayAccounts: env.VITE_FF_DIGITAL_PLAY_ACCOUNTS !== 'false',
    emailProviderAdapter: env.VITE_FF_EMAIL_PROVIDER_ADAPTER !== 'false',
    emailProviderResend: env.VITE_FF_EMAIL_PROVIDER_RESEND !== 'false',
    multiVenueBilling: env.VITE_FF_MULTI_VENUE_BILLING !== 'false',
    secondQuizAddon: env.VITE_FF_SECOND_QUIZ_ADDON !== 'false',
    autoModeAddon: env.VITE_FF_AUTO_MODE_ADDON !== 'false',
    pictureRoundAddon: env.VITE_FF_PICTURE_ROUND_ADDON !== 'false',
    quizToneEnforcement: env.VITE_FF_QUIZ_TONE_ENFORCEMENT !== 'false',
    publicPricing: env.VITE_FF_PUBLIC_PRICING !== 'false',
    dbFirstSelection: env.VITE_FF_DB_FIRST_SELECTION !== 'false',
};

const QUIZ_TEMPLATES = [
    { value: 'classic', label: 'Classic' },
    { value: 'light', label: 'Light (Printer Friendly)' },
    { value: 'outline', label: 'Outline (Printer Friendly)' },
    { value: 'worksheet', label: 'Worksheet (Printer Friendly)' },
    { value: 'ledger', label: 'Ledger (Printer Friendly)' },
    { value: 'broadsheet', label: 'Broadsheet (Printer Friendly)' },
    { value: 'modern', label: 'Modern' },
    { value: 'minimalist', label: 'Minimalist' },
    { value: 'retro', label: 'Retro' },
    { value: 'dark', label: 'Dark' },
    { value: 'bingo', label: 'Bingo Quiz' },
];

export const config = {
    // API Base URL - configured via environment variables
    apiUrl: API_BASE_URL,
    
    // App name and version from environment
    appName: APP_NAME,
    appVersion: APP_VERSION,
    quizTemplates: QUIZ_TEMPLATES,
    
    // API Endpoints
    endpoints: {
        // Auth
        register: '/api/auth/register',
        login: '/api/auth/login',
        refresh: '/api/auth/refresh',
        profile: '/api/auth/profile',
        updateProfile: '/api/auth/profile',
        changePassword: '/api/auth/change-password',
        forgotPassword: '/api/auth/forgot-password',
        resetPassword: '/api/auth/reset-password',
        verifyEmail: '/api/auth/verify-email',
        resendVerification: '/api/auth/resend-verification',
        logout: '/api/auth/logout',
        authSessions: '/api/auth/sessions',
        revokeAuthSession: (id) => `/api/auth/sessions/${id}`,
        
        // Quizzes
        quizzes: '/api/quizzes',
        quizDetail: (id) => `/api/quizzes/${id}`,
        quizApprove: (quizId) => `/api/quizzes/${quizId}/approve`,
        quizFix: (quizId) => `/api/quizzes/${quizId}/fix`,
        quizQuestionUpdate: (quizId, questionId) => `/api/quizzes/${quizId}/questions/${questionId}`,
        quizQuestionRegenerate: (quizId, questionId) =>
            `/api/quizzes/${quizId}/questions/${questionId}/regenerate`,
        quizQuestionSuggestions: (quizId, questionId) =>
            `/api/quizzes/${quizId}/questions/${questionId}/suggestions`,
        quizQuestionRecycle: (quizId, questionId) =>
            `/api/quizzes/${quizId}/questions/${questionId}/recycle`,
        quizStats: '/api/quizzes/stats/bank',
        quizPDF: (id) => `/api/quizzes/${id}/pdf`,

        // Question Bank
        questions: '/api/questions',
        questionClusters: '/api/questions/clusters',
        questionClusterDetail: (id) => `/api/questions/clusters/${id}`,
        questionClusterRecluster: '/api/questions/clusters/recluster',
        questionClusterRunStatus: '/api/questions/clusters/recluster/status',
        questionReviews: '/api/questions/reviews',
        questionReviewDetail: (id) => `/api/questions/reviews/${id}`,
        questionReviewResolve: (id) => `/api/questions/reviews/${id}`,
        questionBulkThemes: '/api/questions/bulk/themes',
        questionBulkTags: '/api/questions/bulk/tags',
        questionSetThemes: (id) => `/api/questions/${id}/themes`,
        questionSetTags: (id) => `/api/questions/${id}/tags`,

        // Taxonomy
        taxonomyCategories: '/api/taxonomy/categories',
        taxonomyThemes: '/api/taxonomy/themes',
        taxonomyCreateTheme: '/api/taxonomy/themes',
        taxonomyUpdateTheme: (id) => `/api/taxonomy/themes/${id}`,
        taxonomyDeleteTheme: (id) => `/api/taxonomy/themes/${id}`,
        
        // Subscriptions
        subscriptionStatus: '/api/subscriptions/status',
        subscriptionConfiguration: '/api/subscriptions/configuration',
        subscriptionPreviewChange: '/api/subscriptions/preview-change',
        subscriptionQuote: '/api/subscriptions/quote',
        createCheckout: '/api/subscriptions/checkout',
        createPortal: '/api/subscriptions/portal',
        cancelSubscription: '/api/subscriptions/cancel',
        reactivateSubscription: '/api/subscriptions/reactivate',
        invoices: '/api/subscriptions/invoices',
        plans: '/api/subscriptions/plans',

        // Account venues
        accountVenues: '/api/account/venues',
        accountVenueDetail: (id) => `/api/account/venues/${id}`,
        accountVenueMembers: (id) => `/api/account/venues/${id}/members`,
        accountVenueMemberDetail: (id, memberId) => `/api/account/venues/${id}/members/${memberId}`,
        accountAuditLog: '/api/account/audit-log',
        supportTickets: '/api/support/tickets',
        supportTicket: (id) => `/api/support/tickets/${id}`,
        supportTicketReplies: (id) => `/api/support/tickets/${id}/replies`,
        
        // Settings
        settings: '/api/settings',
        updateSettings: '/api/settings',
        branding: '/api/settings/branding',
        
        // Analytics
        analyticsDashboard: '/api/analytics/dashboard',
        analyticsQuiz: (id) => `/api/analytics/quiz/${id}`,
        analyticsQuestions: '/api/analytics/questions',
        analyticsOffers: '/api/analytics/offers',
        analyticsThemes: '/api/analytics/themes',
        trackPlay: '/api/analytics/track-play',
        
        // Export
        exportData: '/api/export/data',
        deleteAccount: '/api/export/delete-account',
        exportQuiz: (id) => `/api/export/quiz/${id}`,
        
        // Share
        share: (quizId) => `/api/share/${quizId}`,
        sharePublic: (token) => `/api/share/public/${token}`,
        sharedWithMe: '/api/share/shared-with-me',
        removeCollaborator: (quizId, collaboratorId) => `/api/share/${quizId}/collaborator/${collaboratorId}`,
        
        // Scheduler
        scheduler: '/api/scheduler',
        createSchedule: '/api/scheduler',
        updateSchedule: (id) => `/api/scheduler/${id}`,
        deleteSchedule: (id) => `/api/scheduler/${id}`,
        toggleSchedule: (id) => `/api/scheduler/${id}/toggle`,
        triggerSchedule: (id) => `/api/scheduler/${id}/trigger`,
        scheduleHistory: (id) => `/api/scheduler/${id}/history`,
        quizSlotsWeek: '/api/quiz-slots/week',
        quizSlots: '/api/quiz-slots',
        quizSlotDetail: (id) => `/api/quiz-slots/${id}`,
        quizSlotPause: (id) => `/api/quiz-slots/${id}/pause`,
        quizSlotResume: (id) => `/api/quiz-slots/${id}/resume`,
        quizSlotOccurrenceSkip: (id) => `/api/quiz-slots/occurrences/${id}/skip`,
        quizSlotOccurrenceRetry: (id) => `/api/quiz-slots/occurrences/${id}/retry`,

        // Host Runtime
        hostEvents: '/api/host/events',
        hostEventDetail: (id) => `/api/host/events/${id}`,
        hostStart: (id) => `/api/host/events/${id}/start`,
        hostPause: (id) => `/api/host/events/${id}/pause`,
        hostPrevious: (id) => `/api/host/events/${id}/previous`,
        hostAdvance: (id) => `/api/host/events/${id}/advance`,
        hostReveal: (id) => `/api/host/events/${id}/reveal`,
        hostPresenterLeaderboard: (id) => `/api/host/events/${id}/presenter-leaderboard`,
        hostAddTeam: (id) => `/api/host/events/${id}/teams`,
        hostUpdateTeam: (id, teamId) => `/api/host/events/${id}/teams/${teamId}`,
        hostLinkTeam: (id, teamId) => `/api/host/events/${id}/teams/${teamId}/link`,
        hostRemoveTeam: (id, teamId) => `/api/host/events/${id}/teams/${teamId}`,
        hostResetTeamSession: (id, teamId) => `/api/host/events/${id}/teams/${teamId}/session`,
        hostScores: (id) => `/api/host/events/${id}/scores`,
        hostComplete: (id) => `/api/host/events/${id}/complete`,
        hostLeaderboard: (id) => `/api/host/events/${id}/leaderboard`,
        hostPresenterToken: (id) => `/api/host/events/${id}/presenter-token`,
        hostPresenterState: (token) => `/api/host/presenter/${token}/state`,
        hostOpenQuestion: (id) => `/api/host/events/${id}/open-question`,
        hostCloseQuestion: (id) => `/api/host/events/${id}/close-question`,
        hostSubmissions: (id) => `/api/host/events/${id}/submissions`,
        hostAdjudicateDirect: (id) => `/api/host/events/${id}/adjudicate-direct`,

        // Leagues
        leagueTeams: '/api/leagues/teams',
        leagueTeamDetail: (id) => `/api/leagues/teams/${id}`,
        leagueCurrentSeason: '/api/leagues/seasons/current',
        leagueCreateSeason: '/api/leagues/seasons',
        leagueCloseSeason: (id) => `/api/leagues/seasons/${id}/close`,
        leagueStandings: (id) => `/api/leagues/seasons/${id}/standings`,
        leagueHistory: (id) => `/api/leagues/seasons/${id}/history`,

        // Offers
        offerRedeem: '/api/offers/redeem',

        // Superadmin CRM
        adminUsers: '/api/admin/users',
        adminUser: (id) => `/api/admin/users/${id}`,
        adminUserBillingOverride: (id) => `/api/admin/users/${id}/billing-override`,
        adminUserResendVerification: (id) => `/api/admin/users/${id}/resend-verification`,
        adminUserSetPassword: (id) => `/api/admin/users/${id}/set-password`,
        adminUserImpersonate: (id) => `/api/admin/users/${id}/impersonate`,
        adminUserPasswordReset: (id) => `/api/admin/users/${id}/password-reset`,
        adminUserDelete: (id) => `/api/admin/users/${id}`,
        adminUserRevokeSessions: (id) => `/api/admin/users/${id}/revoke-sessions`,
        adminUserRevokeSession: (id, sessionId) => `/api/admin/users/${id}/sessions/${sessionId}/revoke`,
        adminUserQuizzes: (id) => `/api/admin/users/${id}/quizzes`,
        adminVenueMembers: (id) => `/api/admin/venues/${id}/members`,
        adminVenue: (id) => `/api/admin/venues/${id}`,
        adminVenueMember: (id, memberId) => `/api/admin/venues/${id}/members/${memberId}`,
        adminQuiz: (id) => `/api/admin/quizzes/${id}`,
        adminQuizApprove: (id) => `/api/admin/quizzes/${id}/approve`,
        adminQuizRetry: (id) => `/api/admin/quizzes/${id}/retry`,
        adminQuizDelete: (id) => `/api/admin/quizzes/${id}`,
        adminFinanceOverview: '/api/admin/finance/overview',
        adminFinanceAiUsage: '/api/admin/finance/ai-usage',
        adminOpsOverview: '/api/admin/ops/overview',
        adminFinanceInvoices: '/api/admin/finance/invoices',
        adminFinanceInvoicesExport: '/api/admin/finance/invoices/export',
        adminAudit: '/api/admin/audit',
        adminSupportTickets: '/api/admin/support/tickets',
        adminSupportTicket: (id) => `/api/admin/support/tickets/${id}`,
        adminSupportTicketReplies: (id) => `/api/admin/support/tickets/${id}/replies`,

        adminCrmPubs: '/api/admin/crm/pubs',
        adminCrmPub: (id) => `/api/admin/crm/pubs/${id}`,
        adminCrmPubStage: (id) => `/api/admin/crm/pubs/${id}/stage`,
        adminCrmPubContacts: (id) => `/api/admin/crm/pubs/${id}/contacts`,
        adminCrmContact: (id) => `/api/admin/crm/contacts/${id}`,
        adminCrmPubTasks: (id) => `/api/admin/crm/pubs/${id}/tasks`,
        adminCrmTask: (id) => `/api/admin/crm/tasks/${id}`,
        adminCrmPubNotes: (id) => `/api/admin/crm/pubs/${id}/notes`,
        adminCrmPubActivity: (id) => `/api/admin/crm/pubs/${id}/activity`,
        adminCrmImportPreview: '/api/admin/crm/imports/preview',
        adminCrmImportCommit: '/api/admin/crm/imports/commit',
        adminCrmImport: (id) => `/api/admin/crm/imports/${id}`,

        // Superadmin CMS
        adminCmsPage: (key) => `/api/admin/cms/pages/${key}`,
        adminCmsDraft: (key) => `/api/admin/cms/pages/${key}/draft`,
        adminCmsPublish: (key) => `/api/admin/cms/pages/${key}/publish`,
        adminCmsVersions: (key) => `/api/admin/cms/pages/${key}/versions`,
        adminCmsRollback: (key) => `/api/admin/cms/pages/${key}/rollback`,

        // Superadmin Campaigns
        adminCampaigns: '/api/admin/campaigns',
        adminCampaign: (id) => `/api/admin/campaigns/${id}`,
        adminCampaignSendNow: (id) => `/api/admin/campaigns/${id}/send-now`,
        adminCampaignSchedule: (id) => `/api/admin/campaigns/${id}/schedule`,
        adminCampaignCancel: (id) => `/api/admin/campaigns/${id}/cancel`,
        adminCampaignStats: (id) => `/api/admin/campaigns/${id}/stats`,

        // Public marketing
        publicCmsPage: (key) => `/api/public/cms/pages/${key}`,
        publicLeadCapture: '/api/public/leads',
        publicUnsubscribe: '/api/public/campaigns/unsubscribe',
        publicVenueSearch: '/api/public/venues/search',
        publicVenueDetail: (slug) => `/api/public/venues/${slug}`,
        publicVenueEvents: (slug) => `/api/public/venues/${slug}/events`,
        publicVenueEnquiry: (slug) => `/api/public/venues/${slug}/enquiries`,
        publicPlayJoin: (eventCode) => `/api/public/play/events/${eventCode}/join`,
        publicPlaySessionState: (sessionToken) => `/api/public/play/sessions/${sessionToken}/state`,
        publicPlaySubmitAnswer: (sessionToken) => `/api/public/play/sessions/${sessionToken}/answers`,
        publicPlayLinkAccount: (sessionToken) => `/api/public/play/sessions/${sessionToken}/account-link`,
        publicPlayRegister: '/api/public/play/accounts/register',
        publicPlayLogin: '/api/public/play/accounts/login',
        publicPlayHistory: '/api/public/play/accounts/me/history',
        publicPlayConsent: '/api/public/play/accounts/me/consent',
        publicPlayUnsubscribe: '/api/public/play/accounts/unsubscribe',
        publicPricing: '/api/public/pricing',
        publicCapabilities: '/api/public/capabilities',
        publicStats: '/api/public/stats',
        venuePublicProfile: '/api/venues/public-profile',
        venuePublishPublicEvent: (eventId) => `/api/venues/public-events/${eventId}/publish`,
        venuePublicStats: '/api/venues/public-stats',
        venueGenerateSocialAssets: '/api/venues/social-assets/generate',
        venueSocialAssets: '/api/venues/social-assets',
        venueSocialAssetDetail: (id) => `/api/venues/social-assets/${id}`,
    },
    
    // Token keys (can be customized via environment)
    tokens: {
        access: env.VITE_ACCESS_TOKEN_KEY || 'quizbrew_access_token',
        refresh: env.VITE_REFRESH_TOKEN_KEY || 'quizbrew_refresh_token',
        csrf: 'quizbrew_csrf_token',
    },
    
    // App settings
    app: {
        name: APP_NAME,
        version: APP_VERSION,
        features: FEATURES,
    },
    
    // Pagination defaults
    pagination: {
        defaultLimit: 20,
        maxLimit: 100,
    },
    
    // File upload settings
    upload: {
        maxSize: 10 * 1024 * 1024, // 10MB
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
    },
    
    // Environment info
    environment: env.MODE || 'development',
    isDevelopment: env.DEV || false,
    isProduction: env.PROD || false,
};

const getStorage = (name) => {
    if (typeof globalThis === 'undefined') {
        return null;
    }

    return globalThis[name] || null;
};

const getLocalStorage = () => getStorage('localStorage');
const getSessionStorage = () => getStorage('sessionStorage') || getLocalStorage();

export function getStoredAccessToken() {
    const session = getSessionStorage();
    const local = getLocalStorage();

    const sessionToken = session?.getItem(config.tokens.access);
    if (sessionToken) {
        return sessionToken;
    }

    const localToken = local?.getItem(config.tokens.access);
    if (localToken) {
        session?.setItem(config.tokens.access, localToken);
        local?.removeItem(config.tokens.access);
        return localToken;
    }

    return null;
}

export function setStoredAccessToken(accessToken) {
    clearStoredAccessToken();

    if (!accessToken) {
        return;
    }

    getSessionStorage()?.setItem(config.tokens.access, accessToken);
}

export function clearStoredAccessToken() {
    getSessionStorage()?.removeItem(config.tokens.access);
    getLocalStorage()?.removeItem(config.tokens.access);
}

// Helper to get full API URL
export function getApiUrl(endpoint) {
    return `${config.apiUrl}${endpoint}`;
}

// Helper to check if feature is enabled
export function isFeatureEnabled(feature) {
    return config.app.features[feature] === true;
}

// Helper to get pagination params
export function getPaginationParams(page = 1, limit = config.pagination.defaultLimit) {
    return {
        page: Math.max(1, page),
        limit: Math.min(Math.max(1, limit), config.pagination.maxLimit),
    };
}

// Helper to get auth headers
export function getAuthHeaders() {
    const headers = {
        'Content-Type': 'application/json',
    };

    const csrfToken = localStorage.getItem(config.tokens.csrf);
    if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
    }
    
    return headers;
}

// API request helper with automatic auth and error handling
export async function apiRequest(endpoint, options = {}) {
    const url = getApiUrl(endpoint);
    const method = (options.method || 'GET').toUpperCase();
    
    const defaultHeaders = {
        'Content-Type': 'application/json',
    };

    // Add CSRF token for state-changing requests
    const csrfToken = localStorage.getItem(config.tokens.csrf);
    if (
        csrfToken &&
        ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)
    ) {
        defaultHeaders['X-CSRF-Token'] = csrfToken;
    }
    
    const response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
            ...defaultHeaders,
            ...options.headers,
        },
    });
    
    // Handle rate limiting
    if (response.status === 429) {
        throw new Error('Too many requests. Please wait a moment and try again.');
    }
    
    // Handle authentication errors
    if (
        response.status === 401 &&
        !options._retried &&
        ![
            config.endpoints.login,
            config.endpoints.register,
            config.endpoints.refresh,
            config.endpoints.forgotPassword,
            config.endpoints.resetPassword,
        ].includes(endpoint)
    ) {
        // Token expired or invalid - try to refresh
        if (await tryRefreshToken()) {
            // Retry the original request
            return apiRequest(endpoint, { ...options, _retried: true });
        }
        // Refresh failed - redirect to login
        clearStoredAccessToken();
        localStorage.removeItem(config.tokens.refresh);
        localStorage.removeItem(config.tokens.csrf);
        window.location.href = '/login.html';
        throw new Error('Session expired. Please log in again.');
    }
    
    // Handle other errors
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.error?.message || errorData?.message || `Request failed with status ${response.status}`);
    }
    
    return response.json();
}

// Try to refresh the access token
async function tryRefreshToken() {
    try {
        const response = await fetch(getApiUrl(config.endpoints.refresh), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({}),
        });
        
        if (response.ok) {
            await response.json().catch(() => null);
            setStoredAccessToken(null);
            localStorage.removeItem(config.tokens.refresh);
            localStorage.removeItem(config.tokens.access);
            return true;
        }
    } catch (error) {
        console.error('Token refresh failed:', error);
    }
    
    return false;
}

// Export individual endpoint helpers for convenience
export const api = {
    get: (endpoint, options) => apiRequest(endpoint, { ...options, method: 'GET' }),
    post: (endpoint, body, options) => apiRequest(endpoint, { ...options, method: 'POST', body: JSON.stringify(body) }),
    put: (endpoint, body, options) => apiRequest(endpoint, { ...options, method: 'PUT', body: JSON.stringify(body) }),
    patch: (endpoint, body, options) => apiRequest(endpoint, { ...options, method: 'PATCH', body: JSON.stringify(body) }),
    delete: (endpoint, options) => apiRequest(endpoint, { ...options, method: 'DELETE' }),
};

export default config;
